import type { Page } from '../../types';
import {
  disposalMethodOptions,
  disposalPurchaserTypeOptions,
  disposalYesNoOptions,
} from './data';

const receiptRecording: Page[] = [
  {
    pageType: 'question',
    title: 'Receipt recording',
    name: 'receipt-recording',
    description:
      "Use this section to record sale details. The workbook notes these fields are mandatory for disposals that have reached unconditional status, while method of disposal can be entered earlier if known.",
    questions: [
      {
        questionType: 'textInput',
        title: 'Sold receipt value (GBP)',
        name: 'sold-receipt-value-gbp',
        hint:
          'The gross capital receipt (GBP000,000) delivered at time of sale, either by conditional or unconditional contract. The field should also be completed for sites transferred to Homes England or another central government organisation.',
        placeholder: '0.00',
        rules: {
          required: true,
          float: true,
        },
      },
      {
        questionType: 'select',
        title: 'Method of disposal (i.e. private treaty, informal/formal tender, etc.)',
        name: 'method-of-disposal',
        hintOption: 'Select a method of disposal',
        options: disposalMethodOptions,
        detailsHeading: 'More information',
        detailsContent:
          "State the method of sale used for disposing of the site, for example private treaty, public auction, informal/ formal tender etc. The available drop downs align to the HMG Managing Surplus Government Property guidance (2024). Transfers/ Sales to Homes England should be marked as 'Transfer/ Sale to HE'. Transfers to another central government organisation should be marked as 'Transfer/ Sale to OGD'.",
        rules: {
          required: true,
        },
      },
      {
        questionType: 'radio',
        title: 'Is the contract now unconditional?',
        name: 'is-the-contract-now-unconditional',
        options: disposalYesNoOptions,
        detailsHeading: 'More information',
        detailsContent:
          "Indicate whether the sale contract is now unconditional. If initially sold by conditional contract the response should be 'No' until all contract conditions have been met and the contract becomes unconditional. The receipt can only be scored by the programme at the point when the contract is unconditional.",
        rules: {
          required: true,
        },
      },
      {
        questionType: 'date',
        title: 'Date contract went unconditional',
        name: 'date-contract-went-unconditional',
        hint:
          'The date (DD/MM/YYYY) the contract became unconditional. This is the date of exchange for an unconditional contract, or in the case of a conditional contract the date when all contract conditions have been met. Unconditional contract status date is the date the receipt scores for the Programme.',
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textInput',
        title: 'Name of purchaser',
        name: 'name-of-purchaser',
        hint: 'Name of organisation or individual who has purchased the asset.',
        rules: {
          required: true,
        },
      },
      {
        questionType: 'select',
        title: 'Type of purchaser',
        name: 'type-of-purchaser',
        hintOption: 'Select a purchaser type',
        options: disposalPurchaserTypeOptions,
        hint: 'Identify the type of purchaser from the types listed in the drop down selection.',
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textInput',
        title: 'Sold markers completed?',
        name: 'sold-markers-completed',
        readOnly: true,
        autoFillCondition: {
          trueValue: 'Yes',
          falseValue: 'No',
          conditions: [
            {
              field: 'date-contract-went-unconditional',
              operator: 'notEmpty',
            },
            {
              field: 'sold-receipt-value-gbp',
              operator: 'numberGte',
              value: 0,
            },
            {
              field: 'forecast-receipt-value-confidence-rating',
              operator: 'equalsIgnoreCase',
              value: 'Sold',
            },
            {
              field: 'is-the-contract-now-unconditional',
              operator: 'equalsIgnoreCase',
              value: 'Yes',
            },
            {
              field: 'date-contract-went-unconditional',
              operator: 'dateGte',
              value: '2025-04-01',
            },
            {
              field: 'date-contract-went-unconditional',
              operator: 'dateLte',
              value: '2030-03-31',
            },
          ],
        },
        detailsHeading: 'More information',
        detailsContent: `<p>Users can filter on this data field to see which of their sites have been 'sold'.</p>
<p>This data field records the four main data elements that are required to be 'true' in other responses in the template before a disposal receipt can be scored for the programme:</p>
<ul>
  <li>Forecast receipt value confidence rating = when 'Sold' has been selected from the drop downs</li>
  <li>Sold receipt value = when a GBP value has been entered in the cell</li>
  <li>Is the contract now unconditional? = when the response 'Yes' has been selected from the drop downs</li>
  <li>Date contract went unconditional = when a valid date has been entered in this field i.e. a date between 01/04/2025 and 31/03/2030</li>
</ul>
<p>In addition to the above data points being 'true', OGP uses the 'Method of disposal', 'Name of purchaser' and 'Type of purchaser' fields to exclude any disposals between central government organisations when reporting, as sales/transfers within central government do not score for the programme.</p>`,
        rules: {
          required: true,
        },
      },
    ],
  },
];

export default receiptRecording;
